import React, { useEffect, useState, useRef } from "react";
import Header from "Components/Header/Header";
import Footer from "Components/Footer/Footer";
import TransferFunds from "Components/TransferFunds/TransferFunds";
import useVipApiFetcher from "Utils/Hooks/useVipApiFetcher";
import useFundType, { FUNDTYPE, FUNDTYPE_NAME } from "Utils/Hooks/useFundType";
import Loading from "Components/Loading";
import useSession from "Utils/Hooks/useSession";
import useView, { VIEW } from "Utils/Hooks/useView";
import { findDefaultBank } from "Utils/Utils";
import { useHistory } from "react-router";
import LoadVipAccount from "Components/LoadVipAccount";
import AppLogout from "Utils/Hooks/AppLogout";
import { FUND_SOURCE_TYPE, PRODUCTTYPES } from "Utils/Constants";
import useSessionStorage from "Utils/Hooks/useSessionStorage";
import limitincreaseexclamation from "Assets/Img/limitincrease-exclamation.png";
import "./FundsPage.scss";
import { faro, LogLevel } from "@grafana/faro-react";

const FundsPage = (props) => {
  const vipApi = useVipApiFetcher();
  const fundType = useFundType();
  const { session, fetchSession } = useSession();
  const view = useView();
  const isCashierPage = view === VIEW.cashier;
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [defaultFund, setDefaultFund] = useState();
  const isOperatorPreferred = session?.operatorProducts.includes(
    PRODUCTTYPES.PREFERRED
  );
  const isOperatorOnline = session?.operatorProducts.includes(
    PRODUCTTYPES.ONLINE
  );
  const isOperatorBoth = isOperatorOnline && isOperatorPreferred;
  const patronProductType = session?.patronProductType;
  const patronSelectedProductType = session?.productType;
  const [showMessage, setShowMessage] = useState(
    patronProductType !== patronSelectedProductType &&
      isOperatorBoth &&
      !session?.toggles?.productRoutingEnabled
  );
  const [limitAmount, setLimitAmount] = useState(
    props.withdraw ? session.withdrawLimit : session.depositLimit
  );
  const operatorDailyLimit = props.withdraw
    ? session.withdrawLimit
    : session.depositLimit;
  const [isPlaid, setIsPlaid] = useState(false);
  const [isRepeat, setIsRepeat] = useSessionStorage("isRepeat", false);
  const minAmount = props.withdraw ? session.minWithdraw : session.minDeposit;
  let messages = {
    withdraw: `These funds will be withdrawn from your ${session.operatorName} account and transferred to the account selected.`,
    deposit: `These funds will be added to your ${session.operatorName} account. Your available VIP Preferred limit will decrease when you complete this transaction.`,
  };
  let fundTypeName = "account";
  const fundingSourceList = useRef(null);
  const fundingSources = useRef([]);
  const [preferredPreviousLimit, setPreferredPreviousLimit] = useState(0);
  const [preferredCurrentLimit, setPreferredCurrentLimit] = useState(0);
  const noWarranty = session.noWarranty;
  const [hasVerifiedBank, setHasVerifiedBanks] = useSessionStorage(
    "has-verified-bank",
    false
  );
  const [unVerifiedBank, setDeclinedVerifiedBank] = useSessionStorage(
    "unverifiedbank",
    false
  );

  async function onVipOnlineSubmit(event) {
    if (event) event.preventDefault();
    setShowMessage(false);
    if (
      patronProductType === PRODUCTTYPES.PREFERRED &&
      patronSelectedProductType === PRODUCTTYPES.ONLINE
    ) {
      await SubmitProductTypeChange(PRODUCTTYPES.ONLINE);
    }
    setIsRepeat(true);
  }

  async function onVipPreferredSubmit(event) {
    if (event) event.preventDefault();
    setShowMessage(false);
    if (
      patronProductType === PRODUCTTYPES.ONLINE &&
      patronSelectedProductType === PRODUCTTYPES.PREFERRED
    ) {
      await SubmitProductTypeChange(PRODUCTTYPES.PREFERRED);
      if (!props.withdraw) {
        setLimitAmount(Math.min(operatorDailyLimit, preferredCurrentLimit));
      }
    }
    setIsRepeat(true);
  }

  async function SubmitProductTypeChange(selectedProductType) {
    vipApi
      .fetch("/api/account/switch", "POST", {
        productType: selectedProductType,
      })
      .then(async (response) => {
        if (response.ok) {
          await fetchSession(true);
          let fundList = fundingSourceList.current;
          if (selectedProductType === PRODUCTTYPES.ONLINE) {
            fundList = await vipApi.fetchData("/api/bank/ach");
          }
          if (isOperatorBoth) {
            if (selectedProductType === PRODUCTTYPES.ONLINE) {
              setIsPlaid(true);
              fundList = fundList.filter(
                (fund) =>
                  (fund.achOpenType ===
                    FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                    fund.achOpenType === FUND_SOURCE_TYPE.FINICITY) &&
                  fund.availableLimit
              ); // Get only Plaid accounts
              if (!props.withdraw && fundList.length > 0) {
                setLimitAmount(
                  Math.min(operatorDailyLimit, fundList[0].availableLimit)
                );
              }
              if (!fundList || fundList.length === 0) {
                history.push({
                  pathname: "/addAccount",
                  state: { notRedirect: true },
                });
                return;
              }
            }
            if (selectedProductType === PRODUCTTYPES.PREFERRED) {
              setIsPlaid(false);
            }
          } else if (isOperatorOnline) {
            setIsPlaid(true);
            fundList = fundList.filter(
              (fund) =>
                (fund.achOpenType ===
                  FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                  fund.achOpenType === FUND_SOURCE_TYPE.FINICITY) &&
                fund.availableLimit
            ); // Get only Plaid accounts
            if (!props.withdraw && fundList.length > 0) {
              setLimitAmount(
                Math.min(operatorDailyLimit, fundList[0].availableLimit)
              );
            }
            if (!fundList || fundList.length === 0) {
              history.push({
                pathname: "/addAccount",
                state: { notRedirect: true },
              });
              return;
            }
          } else if (isOperatorPreferred) {
            setIsPlaid(false);
          }
          fundingSources.current = fundList;
          setDefaultFund(findDefaultBank(fundList));
          setIsLoading(false);
        } else {
          faro.api.pushError(new Error("Product Type Switch Error"), {
            context: response,
          });
          if (response.status === 500) {
            console.error(response.statusText);
            history.push({
              pathname: "/error",
            });
          } else if (response.status === 401) {
            console.error(response.statusText);
            history.push({
              pathname: "/expired",
            });
          } else {
            console.error(response.statusText);
            history.push({
              pathname: "/error",
            });
          }
        }
      })
      .catch((error) => {
        console.error(error);
        faro.api.pushError(new Error("Product Type Switch Network Error"), {
          context: error,
        });
        history.push({
          pathname: "/error",
        });
      });
  }

  switch (fundType) {
    case FUNDTYPE.ach:
      fundTypeName = FUNDTYPE_NAME.ach;
      break;
    case FUNDTYPE.netspend:
      messages.deposit = `These funds will be added to your ${session.operatorName} account.`;
      fundTypeName = FUNDTYPE_NAME.netspend;
      break;
    default:
      fundTypeName = "account";
      break;
  }

  useEffect(() => {
    if (!vipApi.ready) return;
    let mounted = true;
    (async () => {
      let fundList = [];
      let vipAccount;
      if (!props.withdraw || session?.toggles?.productRoutingEnabled) {
        vipAccount = await vipApi.fetchData("/api/account");
        if (vipAccount) {
          setLimitAmount(Math.min(limitAmount, vipAccount.availableAmount));
          setPreferredCurrentLimit(vipAccount.limitAmount);
          setPreferredPreviousLimit(vipAccount.vippPreviousLimit);
        }
      }
      if (fundType === FUNDTYPE.ach) {
        fundList = await vipApi.fetchData("/api/bank/ach"); // ACH only
      } else {
        fundList = await vipApi.fetchData("/api/bank"); // Combined list
      }

      fundingSourceList.current = fundList;
      fundingSources.current = fundList;
      setDefaultFund(findDefaultBank(fundList));

      if (session?.toggles?.productRoutingEnabled) {
        const isVipOnlineLimitHigher =
          vipAccount?.onlineAccountsAvailLimit > vipAccount?.availableAmount;
        if (isOperatorBoth) {
          if (isVipOnlineLimitHigher && !noWarranty) {
            if (patronProductType === PRODUCTTYPES.PREFERRED) {
              // Mark the VIP Preferred Patron as VIP Online
              window.sessionStorage.setItem("onlyPlaid", true);
              await SubmitProductTypeChange(PRODUCTTYPES.ONLINE);
            } else {
              if (
                patronSelectedProductType === PRODUCTTYPES.PREFERRED &&
                patronProductType === PRODUCTTYPES.ONLINE
              ) {
                window.sessionStorage.setItem("onlyPlaid", true);
                await SubmitProductTypeChange(PRODUCTTYPES.ONLINE);
              }
              fundList = fundList.filter(
                (fund) =>
                  (fund.achOpenType ===
                    FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                    fund.achOpenType === FUND_SOURCE_TYPE.FINICITY) &&
                  fund.availableLimit
              ); // Get only Plaid accounts
              if (!props.withdraw && fundList.length > 0) {
                setLimitAmount(
                  Math.min(operatorDailyLimit, fundList[0].availableLimit)
                );
              }
              setIsPlaid(true);
              fundingSources.current = fundList;
              setDefaultFund(findDefaultBank(fundList));
              setIsLoading(false);
            }
          } else {
            if (!props.withdraw && noWarranty) {
              const hasbanks = fundList.some(
                (fund) =>
                  fund.achOpenType ===
                    FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                  fund.achOpenType === FUND_SOURCE_TYPE.FINICITY
              );
              if (hasbanks) {
                setHasVerifiedBanks(hasbanks);
              }
              if (fundList && !hasbanks && unVerifiedBank) {
                faro.api.pushLog(
                  ["Declined to add Verified bank, switching to VIPP"],
                  { level: LogLevel.INFO }
                );
                await SubmitProductTypeChange(PRODUCTTYPES.PREFERRED);
                return;
              } else {
                faro.api.pushLog(
                  [
                    "Nowarranty Deposit, prompting patron to connect a verified bank",
                  ],
                  { level: LogLevel.INFO }
                );
                await SubmitProductTypeChange(PRODUCTTYPES.ONLINE);
                return;
              }
            } else if (
              patronProductType === PRODUCTTYPES.ONLINE ||
              (patronSelectedProductType === PRODUCTTYPES.ONLINE &&
                patronProductType === PRODUCTTYPES.PREFERRED)
            ) {
              await SubmitProductTypeChange(PRODUCTTYPES.PREFERRED);
            } else {
              setIsPlaid(false);
              setIsLoading(false);
            }
          }
        } else if (isOperatorOnline) {
          if (patronProductType === PRODUCTTYPES.PREFERRED) {
            // Mark the VIP Preferred Patron as VIP Online
            window.sessionStorage.setItem("onlyPlaid", true);
            await SubmitProductTypeChange(PRODUCTTYPES.ONLINE);
          } else {
            fundList = fundList.filter(
              (fund) =>
                (fund.achOpenType ===
                  FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                  fund.achOpenType === FUND_SOURCE_TYPE.FINICITY) &&
                fund.availableLimit
            ); // Get only Plaid accounts
            if (!props.withdraw && fundList.length > 0) {
              setLimitAmount(
                Math.min(operatorDailyLimit, fundList[0].availableLimit)
              );
            }
            setIsPlaid(true);
            fundingSources.current = fundList;
            setDefaultFund(findDefaultBank(fundList));
            setIsLoading(false);
          }
        } else if (isOperatorPreferred) {
          if (patronProductType === PRODUCTTYPES.ONLINE) {
            await SubmitProductTypeChange(PRODUCTTYPES.PREFERRED);
          } else {
            setIsPlaid(false);
            setIsLoading(false);
          }
        }
      } else {
        if (
          patronProductType === patronSelectedProductType &&
          patronProductType === PRODUCTTYPES.PREFERRED
        ) {
          setIsLoading(false);
        } else if (
          patronProductType === patronSelectedProductType &&
          patronProductType === PRODUCTTYPES.ONLINE
        ) {
          fundList = fundList.filter(
            (fund) =>
              fund.achOpenType === FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
              fund.achOpenType === FUND_SOURCE_TYPE.FINICITY //&& fund.availableLimit
          ); // Get only Plaid accounts

          if (!props.withdraw && fundList.length > 0) {
            setLimitAmount(
              Math.min(operatorDailyLimit, fundList[0].availableLimit)
            );
          }
          setIsPlaid(true);
          fundingSources.current = fundList;
          setDefaultFund(findDefaultBank(fundList));
          setIsLoading(false);
        } else {
          // Requirement document pt.5.c.i
          if (
            isOperatorBoth &&
            patronProductType === PRODUCTTYPES.PREFERRED &&
            patronSelectedProductType === PRODUCTTYPES.ONLINE
          ) {
            if (session.productTypeSwitched) {
              // Mark the VIP Preferred Patron as VIP Online
              window.sessionStorage.setItem("onlyPlaid", true);
              await SubmitProductTypeChange(PRODUCTTYPES.ONLINE);
            } else {
              setIsPlaid(false);
              setIsLoading(false);
            }
          }

          // Requirement document pt.5.c.ii
          if (
            isOperatorBoth &&
            patronProductType === PRODUCTTYPES.ONLINE &&
            patronSelectedProductType === PRODUCTTYPES.PREFERRED
          ) {
            if (session.productTypeSwitched) {
              await SubmitProductTypeChange(PRODUCTTYPES.PREFERRED);
            } else {
              fundList = fundList.filter(
                (fund) =>
                  (fund.achOpenType ===
                    FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                    fund.achOpenType === FUND_SOURCE_TYPE.FINICITY) &&
                  fund.availableLimit
              ); // Get only Plaid accounts
              if (!props.withdraw && fundList.length > 0) {
                setLimitAmount(
                  Math.min(operatorDailyLimit, fundList[0].availableLimit)
                );
              }
              fundingSources.current = fundList;
              setDefaultFund(findDefaultBank(fundList));
              setIsPlaid(true);
              window.sessionStorage.setItem("onlyPlaid", true);
              setIsLoading(false);
            }
          }

          // Requirement document pt.5.c.iii
          if (
            !isOperatorOnline &&
            patronProductType === PRODUCTTYPES.ONLINE &&
            patronSelectedProductType === PRODUCTTYPES.PREFERRED
          ) {
            // Upgrade the VIP Online Patron to VIP Preferred without the pop-up.
            fundingSources.current = fundList;
            setDefaultFund(findDefaultBank(fundList));
            setIsLoading(false);
            setIsPlaid(false);
            await SubmitProductTypeChange(PRODUCTTYPES.PREFERRED);
          }

          // Requirment document pt.5.c.iv
          if (
            !isOperatorPreferred &&
            patronProductType === PRODUCTTYPES.PREFERRED &&
            patronSelectedProductType === PRODUCTTYPES.ONLINE
          ) {
            // Mark the VIP Preferred Patron as VIP Online
            fundList = fundList.filter(
              (fund) =>
                (fund.achOpenType ===
                  FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                  fund.achOpenTypeP === FUND_SOURCE_TYPE.FINICITY) &&
                fund.availableLimit
            ); // Get only Plaid accounts
            if (!props.withdraw && fundList.length > 0) {
              setLimitAmount(
                Math.min(operatorDailyLimit, fundList[0].availableLimit)
              );
            }
            fundingSources.current = fundList;
            setDefaultFund(findDefaultBank(fundList));
            setIsLoading(false);
            setIsPlaid(true);
            window.sessionStorage.setItem("onlyPlaid", true);
            await SubmitProductTypeChange(PRODUCTTYPES.ONLINE);
          }
        }
      }

      if (!mounted) return;
      if (!fundList || fundList.length === 0) {
        history.push({ pathname: "/addAccount", state: { notRedirect: true } });
        return;
      }
    })();
    return () => {
      mounted = false;
    }; // Unmount func
  }, [vipApi.ready]);

  const fundProps = {
    vipApi,
    messages,
    fundingSources,
    defaultFund,
    limitAmount,
    minAmount,
    fundTypeName,
    isPlaid,
    operatorDailyLimit,
    fundingSourceList,
    preferredCurrentLimit,
    preferredPreviousLimit,
    noWarranty,
    unVerifiedBank,
    ...props,
  };

  return (
    <>
      <AppLogout>
        <LoadVipAccount>
          {isLoading ? (
            <Loading />
          ) : (
            <>
              {!isCashierPage ? <Header back={false} /> : ""}
              {!isCashierPage
                ? showMessage &&
                  !isRepeat &&
                  !session.productTypeSwitched && (
                    <div className="switch-message">
                      {patronSelectedProductType === PRODUCTTYPES.PREFERRED && (
                        <>
                          <div
                            style={{
                              width: "10%",
                              display: "inline-block",
                              textAlign: "center",
                            }}
                          >
                            <img
                              src={limitincreaseexclamation}
                              alt="limit-increase"
                            ></img>
                          </div>
                          &nbsp;
                          <div
                            style={{
                              width: "90%",
                              display: "inline-block",
                              textAlign: "auto",
                              "padding-left": "5px",
                            }}
                          >
                            Hello! We noticed you're already a VIP Online
                            member. Continue your transaction to enjoy
                            personalized spending limits based on each of your
                            bank accounts. To enjoy membership benefits and
                            potential higher funding limits,{" "}
                            {
                              <button
                                className="button-link click-switch-to-vipp"
                                onClick={onVipPreferredSubmit}
                              >
                                click here to try VIP Preferred ACH.
                              </button>
                            }
                          </div>
                        </>
                      )}
                      {patronSelectedProductType === PRODUCTTYPES.ONLINE && (
                        <>
                          <div
                            style={{
                              width: "10%",
                              display: "inline-block",
                              textAlign: "center",
                            }}
                          >
                            <img
                              src={limitincreaseexclamation}
                              alt="limit-increase"
                            ></img>
                          </div>
                          &nbsp;
                          <div
                            style={{
                              width: "90%",
                              display: "inline-block",
                              textAlign: "auto",
                              "padding-left": "5px",
                            }}
                          >
                            Hello! We noticed you're already a VIP Preferred ACH
                            member. Continue your transaction to enjoy your
                            membership benefits and potential higher funding
                            limits. To enjoy personalized spending limits based
                            on each of your bank accounts,{" "}
                            {
                              <button
                                className="button-link click-switch-to-vipo"
                                onClick={onVipOnlineSubmit}
                              >
                                click here to try VIP Online.
                              </button>
                            }
                          </div>
                        </>
                      )}
                    </div>
                  )
                : ""}
              <TransferFunds {...fundProps} />
              {!isCashierPage ? <Footer /> : ""}
            </>
          )}
        </LoadVipAccount>
      </AppLogout>
    </>
  );
};

export default FundsPage;
